import { Plus } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { GuidanceCard, type GuidanceStep } from "@/components/ui/guidance-card";
import { toast } from "sonner";

import { useQueryClient } from "@tanstack/react-query";

import {
  asGuidance,
  maxwellThreadByIdKey,
  useCommitMaxwellAction,
  useMaxwellThread,
  useMaxwellThreadById,
  useNewMaxwellThread,
  useSendMaxwellMessage,
} from "@/hooks/useMaxwell";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { Guidance, MaxwellThread } from "@/types/maxwell";

import { MaxwellComposer } from "./MaxwellComposer";
import { MaxwellMessageList } from "./MaxwellMessageList";
import { MaxwellThreadHistory } from "./MaxwellThreadHistory";
import { iconForGuidanceCode } from "./guidance-icons";
import { useMaxwellActions } from "./maxwell-actions";
import { useMaxwellSubject } from "./use-maxwell-subject";

/** Where the chat is rendered — drives density/affordance differences. The rail
 *  shows a compact thread-history + New-chat row inline; the page has more room. */
export type MaxwellSurfaceKind = "rail" | "page";

interface MaxwellChatProps {
  surface: MaxwellSurfaceKind;
}

/**
 * The Maxwell conversation body — pinned guidance card, transcript (streaming,
 * action-preview cards, commit controls), composer, plus the thread-history /
 * New-chat controls. Shared verbatim between the peek rail (`MaxwellRail`) and
 * the workspace page (`MaxwellWorkspacePage`); both render the same thread and
 * state, so switching surfaces never loses the conversation.
 */
export function MaxwellChat({ surface }: MaxwellChatProps) {
  const subject = useMaxwellSubject();
  const { dispatch } = useMaxwellActions();

  // History switching: when a specific older thread is selected, show it;
  // otherwise the latest active thread (the get-or-create default).
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  // Conversation pin: the subject is route-derived, so navigating (say, by
  // clicking a deal inside a Maxwell table) would otherwise swap the rail to
  // that page's thread mid-conversation. The first send pins the thread; it
  // stays on screen across navigation until New chat or a History switch.
  const [pinnedThreadId, setPinnedThreadId] = useState<string | null>(null);
  const historyOrPin = selectedThreadId ?? pinnedThreadId;
  // The subject query stays live even while pinned — it is what a send on the
  // current page renders through, and tearing it down mid-conversation blanks
  // the transcript.
  const { data: latestThread } = useMaxwellThread(subject);
  // The by-id source takes over ONLY when the pin/history selection points
  // somewhere other than the subject's own latest thread — i.e. after a
  // history switch or a navigation away. Swapping sources on the same page
  // (as pin-on-send once did) blanks the screen: the by-id cache starts
  // unfetched and the live stream writes land in the subject cache.
  const usePinnedSource = historyOrPin != null && historyOrPin !== latestThread?.thread_id;
  const { data: selectedThread } = useMaxwellThreadById(
    usePinnedSource ? historyOrPin : undefined,
  );
  const thread = usePinnedSource ? selectedThread : latestThread;

  const {
    send,
    streaming,
    lastGuidance,
    pendingPreview,
    setPendingPreview,
    activeRuns,
    runProgress,
    promoteRun,
  } = useSendMaxwellMessage(thread?.thread_id, subject);
  // Only render checkpoint cards for runs started in the thread currently on
  // screen — a run kicked off in another thread keeps polling in the shared
  // hook state but stays invisible here until its own thread is reopened.
  const activeRunIds = activeRuns
    .filter((r) => r.threadId === thread?.thread_id)
    .map((r) => r.runId);
  const commit = useCommitMaxwellAction(thread?.thread_id, subject);
  const newThread = useNewMaxwellThread(subject);

  const approvePreview = () => {
    if (!pendingPreview) return;
    commit.mutate(
      {
        capability: pendingPreview.capability,
        input: pendingPreview.input,
        commit_token: pendingPreview.commit_token,
      },
      {
        onSuccess: () =>
          setPendingPreview((prev) => (prev ? { ...prev, status: "approved" } : prev)),
        onError: (err) =>
          toast.error(err instanceof ApiError ? err.message : "Failed to apply the action."),
      },
    );
  };

  const rejectPreview = () => {
    setPendingPreview((prev) => (prev ? { ...prev, status: "rejected" } : prev));
  };

  const onAction = (code: string) => dispatch(subject.key, code);
  const guidance = lastGuidance ?? latestThreadGuidance(thread);
  const step = guidance ? toGuidanceStep(guidance, onAction) : null;

  // Sending is what makes a conversation "the user's place" — pin it so
  // navigation can't take it away. The by-id cache is seeded with what's on
  // screen so the pinned source never renders blank when it takes over after
  // navigation. Memoized because the ?ask deep-link effect below depends on it.
  const qc = useQueryClient();
  const sendPinned = useCallback(
    (text: string) => {
      if (thread) {
        qc.setQueryData(maxwellThreadByIdKey(thread.thread_id), thread);
        setPinnedThreadId(thread.thread_id);
      }
      send(text);
    },
    [thread, send, qc],
  );

  const switchThread = (id: string | null) => {
    setSelectedThreadId(id);
    setPinnedThreadId(id);
  };

  const newChat = () => {
    newThread.mutate(undefined, {
      onSuccess: () => {
        setSelectedThreadId(null);
        setPinnedThreadId(null);
      },
    });
  };

  // Deep-link ask: `/maxwell/chat?ask=…` (e.g. the inbox's morning-briefing
  // follow link) sends the question as soon as the thread is ready, then
  // strips the param. Page-surface only — the rail mounts the same component,
  // and both firing would double-send.
  const [searchParams, setSearchParams] = useSearchParams();
  const askConsumed = useRef(false);
  useEffect(() => {
    if (surface !== "page" || askConsumed.current) return;
    const ask = searchParams.get("ask");
    if (!ask || !thread || streaming) return;
    // Deferred a tick (same shape as the DealsPage search debounce): the send
    // and the param strip are event-like reactions to the URL, not render
    // state, so they run outside the effect body proper. The ref makes the
    // send single-fire; the cleanup cancels a superseded timer.
    const t = setTimeout(() => {
      if (askConsumed.current) return;
      askConsumed.current = true;
      sendPinned(ask);
      const next = new URLSearchParams(searchParams);
      next.delete("ask");
      setSearchParams(next, { replace: true });
    }, 0);
    return () => clearTimeout(t);
  }, [surface, searchParams, thread, streaming, sendPinned, setSearchParams]);

  return (
    <div
      className={cn(
        "flex min-h-0 flex-1 flex-col",
        // On the roomy workspace page, constrain the chat to a comfortable
        // reading column; the rail already has a bounded width.
        surface === "page" && "mx-auto w-full max-w-3xl",
      )}
    >
      <div className="flex shrink-0 items-center justify-end gap-2 border-b border-border px-3 py-2">
        <MaxwellThreadHistory
          subject={subject}
          activeThreadId={thread?.thread_id}
          onSwitch={switchThread}
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={newChat}
          disabled={newThread.isPending}
          title="New chat"
        >
          <Plus size={14} />
          New chat
        </Button>
      </div>

      {step && (
        <div className="shrink-0 border-b border-border p-3">
          <GuidanceCard
            steps={[step]}
            stepOverride={
              guidance?.step_index != null
                ? { index: guidance.step_index, total: guidance.step_total }
                : undefined
            }
            workAhead={guidance!.work_ahead.map((c) => ({
              id: c.code,
              label: c.label,
              onClick: () => onAction(c.code),
              kind: "slideout" as const,
            }))}
          />
        </div>
      )}

      <MaxwellMessageList
        messages={thread?.messages ?? []}
        draftText=""
        streaming={streaming}
        pendingPreview={pendingPreview}
        onApprove={approvePreview}
        onReject={rejectPreview}
        committing={commit.isPending}
        activeRunIds={activeRunIds}
        runProgress={runProgress}
        onPromoteRun={promoteRun}
        resetKey={thread?.thread_id}
      />

      <MaxwellComposer onSend={sendPinned} disabled={streaming || !thread} />
    </div>
  );
}

/** Project the server's single current step into a `GuidanceCard` step. */
function toGuidanceStep(g: Guidance, onAction: (code: string) => void): GuidanceStep | null {
  if (!g.current) return null;
  const { code, title, body, tone, actions } = g.current;
  return {
    id: code,
    icon: iconForGuidanceCode(code),
    title,
    description: body,
    isComplete: false,
    tone,
    action:
      actions.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          {actions.map((a) => (
            <Button
              key={a.code}
              type="button"
              size="sm"
              variant={a.kind === "primary" ? "default" : "outline"}
              className={cn(
                a.kind === "destructive" &&
                  "border-destructive-border text-destructive hover:bg-destructive hover:text-white",
              )}
              onClick={() => onAction(a.code)}
            >
              {a.label}
            </Button>
          ))}
        </div>
      ) : undefined,
  };
}

/** The most recent tool_result part's guidance, used before anything streams. */
function latestThreadGuidance(thread: MaxwellThread | undefined): Guidance | null {
  if (!thread) return null;
  for (let i = thread.messages.length - 1; i >= 0; i--) {
    for (const part of thread.messages[i].parts) {
      if (part.kind === "tool_result") {
        const g = asGuidance(part.result);
        if (g) return g;
      }
    }
  }
  return null;
}
